import * as React from 'react';
import Spinner from '@brighthr/component-spinner';
import Icon from '@brighthr/component-icon';
import cn from 'classnames';

type ButtonSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl';

type ButtonChildrenProps = {
	text?: string;
	size?: ButtonSize;
	isLoading?: boolean;
	iconName?: string;
	iconPosition?: 'left' | 'right';
};

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
	ButtonChildrenProps & {
		type?: 'button' | 'submit';
		outlineButton?: boolean;
		textButton?: boolean;
		fullWidth?: boolean;
		color?: 'primary' | 'accent' | 'error';
	};

const ButtonChildren = ({
	size,
	iconPosition,
	isLoading,
	iconName,
	text,
}: ButtonChildrenProps): React.ReactElement => {
	const getIconSize = () => {
		switch (size) {
			case 'xs':
			case 'sm':
			case 'base':
				return 16;
			case 'lg':
			case 'xl':
				return 24;
			default:
				return 16;
		}
	};

	return (
		<>
			{iconPosition === 'left' && (
				<Icon
					size={getIconSize()}
					className={cn('fill-current', { 'mr-2': Boolean(text) })}
					iconName={iconName}
				/>
			)}
			{isLoading && (
				<Spinner
					ariaLabel="Is loading"
					className={cn('mr-2 stroke-current', {
						'ml-2': !text,
						'h-3 w-3': ['xs', 'sm', 'base'].includes(size),
						'h-[18px] w-[18px]': ['lg', 'xl'].includes(size),
					})}
				/>
			)}
			{text}
			{iconPosition === 'right' && (
				<Icon
					size={getIconSize()}
					className={cn('fill-current', { 'ml-2': Boolean(text) })}
					iconName={iconName}
				/>
			)}
		</>
	);
};

export default function Button({
	text = '',
	type = 'button',
	size = 'base',
	outlineButton = false,
	textButton = false,
	isLoading = false,
	fullWidth = false,
	color = 'accent',
	iconName,
	iconPosition,
	...rest
}: ButtonProps): React.ReactElement {
	const buttonClasses = textButton
		? cn(
				'inline-flex font-bold items-center justify-center transition-colors cursor-pointer disabled:cursor-default',
				{
					'text-xs': size === 'xs',
					'text-sm': size === 'sm' || size === 'base',
					'text-base': size === 'lg' || size === 'xl',
					'text-accent-500 hover:text-accent-700 disabled:text-neutral-300':
						color === 'accent',
					'text-primary-700 hover:text-primary-600 disabled:text-neutral-300':
						color === 'primary',
					'text-error-700 hover:text-error-800 disabled:text-neutral-300':
						color === 'error',
					'w-full': fullWidth,
				}
		  )
		: cn(
				'rounded font-bold flex items-center justify-center transition-colors cursor-pointer disabled:cursor-default',
				{
					'px-2.5 py-1.5 text-xs': size === 'xs',
					'px-2.5 py-1.5 text-sm': size === 'sm',
					'px-3.5 py-1.5 text-sm': size === 'base',
					'px-3.5 py-1.5 text-base': size === 'lg',
					'px-[22px] py-2.5 text-base': size === 'xl',
					'bg-white border-2 hover:text-white disabled:border-neutral-300 disabled:bg-white disabled:text-neutral-300':
						outlineButton,
					'text-white border-2 focus:outline focus:outline-2 focus:outline-offset-2 disabled:border-neutral-300 disabled:bg-neutral-300':
						!outlineButton,
					'text-accent-500 border-accent-500 hover:border-accent-700 hover:bg-accent-700 disabled:border-neutral-300 disabled:bg-white disabled:text-neutral-300':
						outlineButton && color === 'accent',
					'text-primary-700 border-primary-700 hover:border-primary-600 hover:bg-primary-600 disabled:border-neutral-300 disabled:bg-white disabled:text-neutral-300':
						outlineButton && color === 'primary',
					'text-error-700 border-error-700 hover:border-error-700 hover:bg-error-700 disabled:border-neutral-300 disabled:bg-white disabled:text-neutral-300':
						outlineButton && color === 'error',
					'bg-accent-500 border-accent-500 hover:border-accent-700 hover:bg-accent-700 focus:outline-accent-500 focus:hover:outline-accent-700 disabled:border-neutral-300 disabled:bg-neutral-300':
						!outlineButton && color === 'accent',
					'bg-error-700 border-error-700 hover:border-error-900 hover:bg-error-900 focus:outline-error-700 focus:hover:outline-error-900 disabled:border-neutral-300 disabled:bg-neutral-300':
						!outlineButton && color === 'error',
					'bg-primary-700 border-primary-700 hover:border-primary-600 hover:bg-primary-600 focus:outline-primary-700 focus:hover:outline-primary-600 disabled:border-neutral-300 disabled:bg-neutral-300':
						!outlineButton && color === 'primary',
					'w-full': fullWidth,
				}
		  );

	// TS linter does not like dynamic button types
	// Docs/Forum recommend rendering based on type
	if (type === 'submit') {
		return (
			<button type="submit" className={buttonClasses} {...rest}>
				<ButtonChildren
					text={text}
					isLoading={isLoading}
					iconName={iconName}
					iconPosition={iconPosition}
					size={size}
				/>
			</button>
		);
	}

	return (
		<button type="button" className={buttonClasses} {...rest}>
			<ButtonChildren
				text={text}
				isLoading={isLoading}
				iconName={iconName}
				iconPosition={iconPosition}
				size={size}
			/>
		</button>
	);
}
